import { Box, Flex, Select } from "@chakra-ui/react";
import { RowData, Table as ReactTable } from "@tanstack/react-table";
import {
  MdFirstPage,
  MdLastPage,
  MdNavigateBefore,
  MdNavigateNext,
} from "react-icons/md";
import NumberInput from "components/Shared/Forms/NumberInput";
import { formatInteger } from "utils/formatting";
import SkeletonSingleLineText from "components/Shared/SkeletonSingleLineText";
import PaginationControlButton from "./PaginationControlButton";

type Props<D extends RowData> = {
  isLoading?: boolean;
  pageSizeOptions?: number[] | readonly number[] | null;
  tableInstance: ReactTable<D>;
};

export const defaultPageSizeOptions = [5, 10, 25, 50, 100] as const;

export default function PaginationControls<D extends RowData>({
  isLoading = false,
  pageSizeOptions = defaultPageSizeOptions,
  tableInstance,
}: Props<D>) {
  const {
    pagination: { pageIndex, pageSize },
  } = tableInstance.getState();

  const pageCount = tableInstance.getPageCount();

  const canPrevPage = tableInstance.getCanPreviousPage();
  const canNextPage = tableInstance.getCanNextPage();

  const pageCountDisplay = Math.max(pageCount, 1);

  return (
    <Box
      flex="1 0 10rem"
      sx={{
        containerType: "inline-size",
        containerName: "pagination-controls",
        "@container pagination-controls (max-width: 24rem)": {
          ".pagination-controls": { flexWrap: "wrap" },
          ".pagination-controls-inputs": { order: 1, flex: "1 1 100%" },
        },
      }}
    >
      <Flex
        alignItems="center"
        justifyContent="center"
        className="pagination-controls"
        gap={2}
      >
        <Flex gap={1}>
          <PaginationControlButton
            icon={<MdFirstPage />}
            label="First Page"
            onClick={() => tableInstance.setPageIndex(0)}
            isDisabled={!canPrevPage}
          />
          <PaginationControlButton
            icon={<MdNavigateBefore />}
            label="Previous Page"
            onClick={() => tableInstance.previousPage()}
            isDisabled={!canPrevPage}
          />
        </Flex>

        <Flex
          mx={2}
          alignItems="baseline"
          justifyContent="center"
          whiteSpace="nowrap"
          flexWrap="wrap"
          flex="1 0 12em"
          maxW="30em"
          color="gray.700"
          fontWeight={300}
          fontSize="xs"
          gap={2}
          className="pagination-controls-inputs"
        >
          <Flex alignItems="baseline" mx={2}>
            Page
            <NumberInput
              value={pageIndex + 1}
              onChange={(value) =>
                tableInstance.setPageIndex((value as number) - 1)
              }
              isDisabled={!pageCount || (pageCount === 1 && pageIndex === 0)}
              min={1}
              max={pageCountDisplay}
              thousandSeparator
              w="4.6em"
              mx=".5em"
              textAlign="right"
              size="sm"
            />
            of
            <SkeletonSingleLineText
              isLoaded={!isLoading}
              isTextPresenceIgnored
              mx=".5em"
              my={0}
              minW="1.25em"
              text={
                typeof pageCount === "number"
                  ? formatInteger(pageCountDisplay)
                  : null
              }
            />
          </Flex>

          {!!pageSizeOptions?.length && (
            <Flex alignItems="baseline" mx={2}>
              <Select
                w="5.75em"
                mx=".5em"
                textAlign="right"
                size="sm"
                value={pageSize}
                onChange={(e) => {
                  const newPageSize =
                    e.target.value && parseInt(e.target.value, 10);
                  if (newPageSize) tableInstance.setPageSize(newPageSize);
                }}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {formatInteger(size)}
                  </option>
                ))}
              </Select>
              per page
            </Flex>
          )}
        </Flex>

        <Flex gap={1}>
          <PaginationControlButton
            icon={<MdNavigateNext />}
            label="Next Page"
            onClick={() => tableInstance.nextPage()}
            isDisabled={!canNextPage}
          />
          <PaginationControlButton
            icon={<MdLastPage />}
            label="Last Page"
            onClick={() => tableInstance.setPageIndex(pageCount - 1)}
            isDisabled={!canNextPage}
          />
        </Flex>
      </Flex>
    </Box>
  );
}
